import { getCookie, setCookie } from "@app/class/Utilities";
import { HybridServiceAccessor } from "@app/common/hybrid-service-accessor";
import { UsersService } from "@app/services/users.service";
import { MessageService } from "@components/messaging/service/messaging.service";
import { DateRange } from "@enums/DateRange.enum";
import { IUser, UserPlatform } from "@interfaces/IUser";
import { AccountService, IAccount } from "@services/account.service";
import { AccountFeatureService, FeatureAccessType, UserRole } from "@services/accountFeature.service";
import { AccountOption } from "@services/class/AccountOption";
import { ConstantsService } from "@services/constantsService";
import { CurrentUserService } from "@services/currentUser.service";
import { SessionService } from "@services/session.service";
import { TranslationService } from "@services/translation.service";
import { VeriDate } from "@services/veriDate.service";
import { TemperatureNavigationModule } from "@temperature/temperature.navigation";
import { TmsModuleNavigation } from "@tms/tms.navigation";
import { StateService } from "@uirouter/angularjs";
import * as angular from "angular";
import { PageStateNames } from "src/app/class/PageStateNames";
import { SettingsName } from "src/app/class/SettingsName";
import { CameraModule } from "src/app/main/modules/cameras/camera.module.ajs";
import { AccountFeatures } from "../../enum/account/AccountFeature";
import { UserType } from "../../enum/UserType";
import { ExploreNavigationModule } from "../components/insights/insights.navigation";
import { ReportsNavigationModule } from "../components/reports/reports.navigation";
import { ResourceManagementNavigationModule } from "../components/resourceManagement/resourceManagement.navigation";
import { VideoEventCode } from "../components/videos/enums/VideoEventCode";
import { VideoStatus } from "../components/videos/enums/VideoStatus";
import { IVideoEntry } from "../components/videos/models/IVideoEntry";
import { VideoEntryService } from "../components/videos/services/video-entry.service";
import { VideoReviewService } from "../components/videos/services/video-review.service";
import { VideoEntryUtils } from "../components/videos/utils/video-entry-utils";
import { AlertsNavigationModule } from "../modules/alerts/alerts.navigation";
import { DashboardNavigationService, DashboardNavItem } from "../modules/dashboard/dashboard-navigation.service";
import { FleetNavigationModule } from "../modules/fleet/fleet.navigation";
import { FoundationNavigationModule } from "../modules/foundation/foundation.navigation";
import { MaintenanceModule } from "../modules/maintenance/maintenance.module.ajs";

type NavigationListenerCallback = (INavigationDeclarationObject) => angular.IPromise<any>;

export interface INavigationItem {
    icon: string;
    label: string;
    uiSref: string;
    subNavItems?: INavigationItem[];

    new?: boolean;
    hasNewSubItem?: boolean;

    idParam?: string;

    expanded?: boolean;
    divider?: boolean;
    count?: number;
    countSuffix?: string;
    show?: boolean;

    requiredFeatures?: AccountFeatures[];
    listener?: angular.IPromise<any>;
    createListenerCallback?: NavigationListenerCallback;
}

export interface INavigationItemArguments {
    subNavItems?: INavigationItem[];
    roles?: string[];

    onlyShowOnTMS?: boolean;
    hideInProduction?: boolean;
    showOnMobile?: boolean;
    hideFromReseller?: boolean;
    hideIfNotInternalOrFoundation?: boolean;

    className?: string;
    divider?: boolean;

    idParam?: string;

    requiredReadFeatures?: AccountFeatures[];
    requiredManageFeatures?: AccountFeatures[];

    skipAccountFeatureCheck?: boolean;
    new?: boolean;
}

export interface INavigationDeclarationObject {
    accountOptions?: AccountOption;
    currentUser?: IUser;
    accountData?: IAccount;

    navigationItems?: INavigationItem[];
}

export class SideNavigationService {
    private readonly videoReviewService = new HybridServiceAccessor<VideoReviewService>("videoReviewService", this.$injector);
    private readonly videoEntryService = new HybridServiceAccessor<VideoEntryService>("videoEntryService", this.$injector);
    private readonly dashboardNavigationService = new HybridServiceAccessor<DashboardNavigationService>("dashboardNavigationService", this.$injector);

    private dashboards: DashboardNavItem[] = [];

    isLocalDevelopment: boolean;
    isProduction: boolean;
    isMobile: boolean;
    currentUser: IUser;

    visitedCookie: string = "visitedPages";

    static $inject = ["$q", "$interval", "$rootScope", "$state", "sessionService", "accountService", "accountFeatureService", "currentUserService", "usersService", "constantsService", "messageService", "veriDate", "translationService", "$injector"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $interval: angular.IIntervalService,
        private readonly $rootScope: angular.IScope,
        private readonly $state: StateService,
        private readonly sessionService: SessionService,
        private readonly accountService: AccountService,
        private readonly accountFeatureService: AccountFeatureService,
        private readonly currentUserService: CurrentUserService,
        private readonly usersService: UsersService,
        private readonly constantsService: ConstantsService,
        private readonly messageService: MessageService,
        private readonly veriDate: VeriDate,
        private readonly translationService: TranslationService,
        private readonly $injector: angular.auto.IInjectorService,
    ) {

        this.isMobile = this.constantsService.getIsMobile();
        this.isLocalDevelopment = this.constantsService.getIsLocalDevelopment();
        this.isProduction = this.constantsService.getIsProduction();
    }

    async createNavigationForUser(): Promise<INavigationDeclarationObject> {
        const currentUserPromise = this.currentUserService.getCurrentUser() as Promise<IUser>;
        const accountDataPromise = this.accountService.getAccountData() as Promise<IAccount>;

        const [currentUser, accountData] = await Promise.all([currentUserPromise, accountDataPromise]);

        this.currentUser = currentUser;
        const response: INavigationDeclarationObject = {};

        response.accountOptions = this.sessionService.getItem<AccountOption>(SettingsName.AccountOptions);
        response.currentUser = currentUser;
        response.accountData = accountData;

        if (currentUser.platform == UserPlatform.Foundation) {

            if (currentUser.Type == UserType.InternalUser) {
                response.navigationItems = this.createNavigationItemsForFoundation(response);
            }
            else {
                response.navigationItems = this.createNavigationItemsForFoundationEngineers(response)
            }
        }
        else {
            this.dashboards = await this.dashboardNavigationService.service.getNavItems(true);

            response.navigationItems = this.createNavigationItemsForUser(response);
            this.createDebugListenerForUser(response);
        }

        return response;
    }

    removeNavItemNewStatus(navItem: INavigationItem): void {
        let visitedPages = getCookie(this.visitedCookie);
        visitedPages += `&${navItem.uiSref}`;

        setCookie(this.visitedCookie, visitedPages, 30);

        navItem.new = false;
    }

    // ---

    private createNavigationItemsForUser(navObject: INavigationDeclarationObject): INavigationItem[] {
        const navigationItems: INavigationItem[] = [];

        this.addNavigationItem(
            navigationItems,
            navObject,
            this.translationService.t("Common.Dashboards"),
            "app.dashboard",
            "fa-gauge",
            {
                roles: [UserRole.ShowReports],
                subNavItems: this.createDashboardNavigationItems(navObject),
                new: this.showNew("app.dashboard")
            }
        );


        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Fleet"), FleetNavigationModule.ParentRoot, "fa-truck", {
            subNavItems: this.getFleetSubNavItems(navObject),
            requiredReadFeatures: [
                AccountFeatures.Breathalock,
                AccountFeatures.Messaging,
                AccountFeatures.Tacho,
                AccountFeatures.TrackingDevice
            ]
        });

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Explore"), ExploreNavigationModule.ParentRoot, "fa-lightbulb", {
            subNavItems: this.getExploreSubNavItems(navObject),
            requiredReadFeatures: [AccountFeatures.TrackingDevice],
            roles: [UserRole.History],
            new: this.showNew(ExploreNavigationModule.ParentRoot)
        });

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Videos.Cameras"), CameraModule.ParentRoot, "fa-video", { subNavItems: this.createCameraNavigationItems(navObject), requiredReadFeatures: [AccountFeatures.Camera] }, this.createVideoListener.bind(this));
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.TMS"), TmsModuleNavigation.ParentRoot, "fa-route fad", { subNavItems: this.createTmsNavigationItems(navObject), requiredReadFeatures: [AccountFeatures.TMS] });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Temperature"), TemperatureNavigationModule.ParentRoot, "fa-snowflake fad", { subNavItems: this.createTemperatureNavigationItems(navObject), requiredReadFeatures: [AccountFeatures.Temperature], showOnMobile: true });
        //this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Maintenance"), MaintenanceModule.ParentRoot, "fa-toolbox", { subNavItems: this.getMaintenanceSubNavItems(navObject), requiredReadFeatures: [AccountFeatures.Can], roles: [UserRole.History], new: this.showNew(MaintenanceModule.ParentRoot) });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Maintenance"), MaintenanceModule.Overview, "fa-toolbox", { showOnMobile: false, requiredReadFeatures: [AccountFeatures.Can, AccountFeatures.Ebpms], new: this.showNew(MaintenanceModule.ParentRoot) });

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Geofences"), FleetNavigationModule.Geofences, "fa-circle-nodes fad", { roles: [UserRole.ManageGeofences] });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Reporting"), ReportsNavigationModule.ParentRoot, "fa-square-poll-vertical fad", { subNavItems: this.createReportNavigationItems(navObject), roles: [UserRole.ShowReports] });

        // Divider
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Alerts"), AlertsNavigationModule.ParentRoot, "fa-bell", { subNavItems: this.createAlertNavigationItems(navObject), roles: [UserRole.ManageAlerts, UserRole.ManageAlertReviews], divider: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Admin"), ResourceManagementNavigationModule.ParentRoot, "fa-gear fad", { subNavItems: this.createAdminNavigationItems(navObject), roles: [UserRole.ManageDrivers, UserRole.ManageVehicles, UserRole.ManageGroups, UserRole.Administrator] });

        const diagnostics = this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Diagnostics"), PageStateNames.COMPONENTS_DIAGNOSTICS, "fa-wrench");
        if (diagnostics) {
            if (diagnostics) {
                diagnostics.show = false;
            }

            return navigationItems;
        }
    }

    private createDashboardNavigationItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        const navigationItems: INavigationItem[] = [];

        this.dashboards.forEach(dashboard => {

            this.addNavigationItem(
                navigationItems,
                navObject,
                dashboard.dashboard.Title,
                "app.dashboard.id",
                "",
                { idParam: dashboard.id, showOnMobile: true });
        });

        return navigationItems;
    }

    private createDebugListenerForUser(navObject: INavigationDeclarationObject) {
        this.$rootScope.$on("debugmode", () => {
            var diagnostics = this.findNavItem(navObject.navigationItems, PageStateNames.COMPONENTS_DIAGNOSTICS);
            this.$state.go(PageStateNames.COMPONENTS_DIAGNOSTICS);

            if (diagnostics) {
                diagnostics.show = true;
            }
        });
    }

    private addNavigationItem(navigationItems: INavigationItem[], navObject: INavigationDeclarationObject, label: string, uiSref: string, icon: string, args?: INavigationItemArguments, addListenerCallback?: NavigationListenerCallback): INavigationItem {
        if (args) {
            if (args.requiredReadFeatures?.length && (!args.skipAccountFeatureCheck && !this.accountFeatureService.accountHasAnyFeatureAccess(navObject.accountData, args.requiredReadFeatures) || !this.accountFeatureService.currentUserHasAnyFeatureAccess(args.requiredReadFeatures, FeatureAccessType.Read, args.skipAccountFeatureCheck))) {
                return;
            }

            if (args.requiredManageFeatures?.length && (!args.skipAccountFeatureCheck && !this.accountFeatureService.accountHasAnyFeatureAccess(navObject.accountData, args.requiredManageFeatures) || !this.accountFeatureService.currentUserHasAnyFeatureAccess(args.requiredManageFeatures, FeatureAccessType.Manage, args.skipAccountFeatureCheck))) {
                return;
            }

            if (args.hideFromReseller && this.currentUserService.cachedUser.IsReseller) {
                return;
            }

            if (args.hideIfNotInternalOrFoundation && !this.usersService.isInternalOrFoundation()) {
                return;
            }

            if (args.hideInProduction) {
                args.className += " hide-in-production";
            }

            if (this.isProduction && args.hideInProduction) {
                return;
            }

            if (args.roles && !this.accountFeatureService.currentUserHasRole(args.roles, false)) {
                return;
            }

            if (args.onlyShowOnTMS !== undefined) {
                var tmsEnabledOnAccount = navObject.accountOptions?.Tms;

                if (args.onlyShowOnTMS && !tmsEnabledOnAccount || !args.onlyShowOnTMS && tmsEnabledOnAccount) {
                    return;
                }
            }
        }

        var navItem: INavigationItem = {
            label: label,
            uiSref: uiSref,
            icon: icon,
            divider: args?.divider,
            subNavItems: args?.subNavItems,
            show: !this.isMobile || args?.showOnMobile || args?.subNavItems?.some(i => i.show),
            expanded: this.isMobile && args?.subNavItems?.filter(i => i.show).length <= 2,
            hasNewSubItem: args?.subNavItems?.some(i => i.new),
            idParam: args?.idParam,
            createListenerCallback: addListenerCallback
        };

        navItem = Object.assign(navItem, args);

        navigationItems.push(navItem);

        return navItem;
    }

    private getFleetSubNavItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        var navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Vehicles"), FleetNavigationModule.Vehicles, "directions_car", { requiredReadFeatures: [AccountFeatures.TrackingDevice], showOnMobile: true });

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Messages"), FleetNavigationModule.Messages, "email", { requiredReadFeatures: [AccountFeatures.Messaging] }, this.createMessagingListener.bind(this));
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Planning"), FleetNavigationModule.Planning, "work", { skipAccountFeatureCheck: true, onlyShowOnTMS: false, requiredReadFeatures: [AccountFeatures.TMS] });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Tachograph"), FleetNavigationModule.Tachograph, "query_builder", { roles: [UserRole.History], requiredReadFeatures: [AccountFeatures.Tacho], showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Breathalock"), FleetNavigationModule.Breathalock, "liquor", { roles: [UserRole.History], requiredReadFeatures: [AccountFeatures.Breathalock] });
        //this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Maintenance"), FleetNavigationModule.Maintenance, "ballot", { roles: [UserRole.History] });

        return navigationItems;
    }

    private createMessagingListener(navObject: INavigationItem): angular.IPromise<any> {
        this.updateMessageCounts();
        const messageUpdateInterval = this.$interval(() => { this.updateMessageCounts() }, 5 * 60 * 1000);

        this.$rootScope.$on(MessageService.UnreadMessageCountUpdatedEvent, (event: angular.IAngularEvent, numUnreadMessages: number) => {
            this.updateMessageCount(navObject.subNavItems, numUnreadMessages);
        });

        return messageUpdateInterval;
    }

    private createVideoListener(navObject: INavigationItem): angular.IPromise<any> {
        this.updateVideoCounts(navObject.subNavItems);
        const videoUpdateInterval = this.$interval(() => { this.updateVideoCounts(navObject.subNavItems, true) }, 2 * 60 * 1000);

        this.videoReviewService.service.unseenVideoCountUpdated$.subscribe(() => {
            this.updateVideoCounts(navObject.subNavItems, true);
        })

        this.videoEntryService.service.downloadVideoCountUpdated$.subscribe(() => {
            this.updateVideoCounts(navObject.subNavItems, true);
        });

        return videoUpdateInterval;
    }

    private getExploreSubNavItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        var navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("SideNavigation.FleetHistory"), ExploreNavigationModule.FleetHistory, "email");
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("SideNavigation.FleetUtilisation"), ExploreNavigationModule.FleetUtilisation, "work");
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("SideNavigation.FleetEvents"), ExploreNavigationModule.FleetEvents, "query_builder");
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("SideNavigation.FindNearest"), ExploreNavigationModule.FindNearest, "liquor");
        // Coming soon
        //this.addNavigationItem(navigationItems, navObject, "Driver Behaviour", ExploreNavigationModule.DriverBehaviour, "ballot", { new: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("SideNavigation.DepotCompliance"), ExploreNavigationModule.DepotCompliance, "ballot");

        return navigationItems;
    }

    private createAlertNavigationItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        let navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.History"), AlertsNavigationModule.AlertHistory, null, { roles: [UserRole.Administrator, UserRole.ManageAlerts], showOnMobile: false });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Alerts.ActionRequired"), AlertsNavigationModule.ReviewAlerts, null, { roles: [UserRole.Administrator, UserRole.ManageAlertReviews], showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Alerts.Compliance"), AlertsNavigationModule.Compliance, null, { roles: [UserRole.Administrator, UserRole.ManageAlertReviews], showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Alerts.ManageAlerts"), AlertsNavigationModule.ManageAlerts, null, { roles: [UserRole.Administrator, UserRole.ManageAlerts], showOnMobile: false });

        return navigationItems;
    }

    private createReportNavigationItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        let navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Reports.ViewReports"), ReportsNavigationModule.ReportList, null, { roles: [UserRole.ShowReports], showOnMobile: false });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.History"), ReportsNavigationModule.History, "payments", { roles: [UserRole.History], new: this.showNew(ReportsNavigationModule.History) });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Reports.ViewSchedules"), ReportsNavigationModule.ReportSchedules, "payments", { roles: [UserRole.History] });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Expenses.Title"), ReportsNavigationModule.Expenses, "payments", { roles: [UserRole.History] });

        return navigationItems;
    }

    private createTemperatureNavigationItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        const navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Vehicles"), TemperatureNavigationModule.TemperatureCompliance, "thermostat", { requiredReadFeatures: [AccountFeatures.Temperature], showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Temperature.Reefer"), TemperatureNavigationModule.Reefer, "kitchen", { requiredReadFeatures: [AccountFeatures.Reefer], showOnMobile: false });

        return navigationItems;
    }

    private createTmsNavigationItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        var navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Tms.ActiveRoutes"), TmsModuleNavigation.ActiveRoutes, "event_available", { showOnMobile: false });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Tms.ArrivalsBoard"), TmsModuleNavigation.ArrivalsBoard, "maps_home_work", { showOnMobile: false });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Tms.History"), TmsModuleNavigation.History, "history", { showOnMobile: false });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Tms.RoutePerformance"), TmsModuleNavigation.RoutePerformance, "history", { showOnMobile: false });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Tms.VisitPerformance"), TmsModuleNavigation.VisitPerformance, "history", { showOnMobile: false });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Tms.Scheduling"), TmsModuleNavigation.Scheduling, "event", { showOnMobile: false });

        return navigationItems;
    }

    private getMaintenanceSubNavItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        var navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Dashboard"), MaintenanceModule.Dashboard, "", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Overview"), MaintenanceModule.Overview, "", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Overview"), MaintenanceModule.EBPMS, "", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Overview"), MaintenanceModule.TPMS, "", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Overview"), MaintenanceModule.VOR, "", { showOnMobile: true });

        return navigationItems;
    }

    private createAdminNavigationItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        let navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Groups"), ResourceManagementNavigationModule.GroupsManagement, "folder", { roles: [UserRole.ManageGroups, UserRole.Administrator] });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Vehicles"), ResourceManagementNavigationModule.VehiclesManagement, "directions_car", { roles: [UserRole.ManageVehicles, UserRole.Administrator] });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Drivers"), ResourceManagementNavigationModule.DriversManagement, "supervisor_account", { roles: [UserRole.ManageDrivers, UserRole.Administrator] });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Users"), ResourceManagementNavigationModule.UsersManagement, "manage_accounts", { roles: [UserRole.Administrator] });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Videos.Cameras"), ResourceManagementNavigationModule.CameraManagement, "videocam", { roles: [UserRole.Administrator, UserRole.ManageVehicles], requiredReadFeatures: [AccountFeatures.Camera] });

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.DrivingStyles"), ResourceManagementNavigationModule.DrivingStylesManagement, "insights", { roles: [UserRole.Administrator] });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.DeviceProfiles"), ResourceManagementNavigationModule.DeviceProfilesManagement, "data_thresholding", { roles: [UserRole.Administrator] });

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Settings"), ResourceManagementNavigationModule.Settings, "settings", { roles: [UserRole.Administrator], hideIfNotInternalOrFoundation: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.ContextualSpeeding"), ResourceManagementNavigationModule.ContextualSpeedingSettings, "settings", { roles: [UserRole.Administrator], hideIfNotInternalOrFoundation: true, requiredReadFeatures: [AccountFeatures.ContextualSpeeding] });

        return navigationItems;
    }

    private createCameraNavigationItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        var navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Videos.ReviewEvents"), CameraModule.Events, "notifications_active");

        const videoItem = this.addNavigationItem(navigationItems, navObject, this.translationService.t("Videos.Videos"), CameraModule.Videos, "videocam", { showOnMobile: true });

        if (videoItem) {
            videoItem.countSuffix = "New";
        }

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Videos.Coaching"), CameraModule.Coaching, "supervisor_account");
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Vehicles"), CameraModule.Vehicles, "directions_car", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Drivers"), CameraModule.Drivers, "person");
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Videos.Downloads"), CameraModule.Downloads, "get_app", { showOnMobile: true });

        return navigationItems;
    }

    private updateVideoCounts(navigationItems: INavigationItem[], invalidateCache: boolean = false) {
        var dateRange = this.veriDate.dateRangeFromEnum(DateRange.Latest7Days);

        const eventsPromise = this.videoEntryService.service.getAllVideosEntries(dateRange.start, dateRange.end, { processEntries: true, invalidateCache }) as angular.IPromise<IVideoEntry[]>;
        const requestedPromise = this.videoEntryService.service.getVideoEntriesRequested({ invalidateCache }) as angular.IPromise<IVideoEntry[]>;

        this.$q.all([eventsPromise, requestedPromise]).then(([videoEntries, requestedEntries]) => {
            this.updateNavItemCount(navigationItems, CameraModule.Videos, videoEntries.filter(v => VideoEntryUtils.isUnseen(v)).length);
            this.updateNavItemCount(navigationItems, CameraModule.Events, videoEntries.filter(v => VideoEntryUtils.isEventAndActive(v) && v.Status != VideoStatus.Restricted && v.EventCode != VideoEventCode.Unknown && v.EventCode != VideoEventCode.Exception).length);
            this.updateNavItemCount(navigationItems, CameraModule.Coaching, videoEntries.filter(v => VideoEntryUtils.needsReview(v)).length);
            this.updateNavItemCount(navigationItems, CameraModule.Downloads, requestedEntries.filter(v => VideoEntryUtils.isDownloading(v)).length);
        });
    }

    private updateMessageCounts() {
        this.messageService
            .getMessageUnreadCounts()
            .then(unreadMessageCounts => {
                //this.parseNewMessages(unreadMessageCounts);
                //console.log(unreadMessageCounts);
            });
    }

    private updateMessageCount(navigationItems: INavigationItem[], numUnreadMessages: number) {
        this.updateNavItemCount(navigationItems, FleetNavigationModule.Messages, numUnreadMessages);
    }

    private createNavigationItemsForFoundation(navObject: INavigationDeclarationObject): INavigationItem[] {
        var navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Customers"), FoundationNavigationModule.Customers, "fa-building", { subNavItems: this.createCustomerNavigationItems(navObject), showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Commissioning"), FoundationNavigationModule.Jobs, "fa-screwdriver-wrench", { subNavItems: this.createInstallsNavigationItems(navObject), showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("SideNavigation.Hardware"), FoundationNavigationModule.Vehicles, "fa-truck", { subNavItems: this.createHardwareNavigationItems(navObject), showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("SideNavigation.DeviceHealth"), FoundationNavigationModule.DeviceHealth, "fa-heart-pulse", { subNavItems: this.createDeviceHealthNavigationItems(navObject), showOnMobile: true });

        return navigationItems;
    }

    private createInstallsNavigationItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        var navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.NewCommission"), FoundationNavigationModule.NewCommissionNoBindings, null, { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Jobs"), FoundationNavigationModule.Jobs, "work", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.History"), FoundationNavigationModule.Commissioning, "view_in_ar", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("SideNavigation.Engineers"), FoundationNavigationModule.Engineers, "settings", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("VisitFailures.VisitFailures"), FoundationNavigationModule.VisitFailures, "", { showOnMobile: true });

        return navigationItems;
    }

    private createHardwareNavigationItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        var navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Vehicles"), FoundationNavigationModule.Vehicles, "directions_car", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("FoundationUnits.Units"), FoundationNavigationModule.Units, "token", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Cameras"), FoundationNavigationModule.Cameras, "videocam", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Peripherals"), FoundationNavigationModule.Peripherals, "token", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("FoundationSims.Sims"), FoundationNavigationModule.Sims, "", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("RVLite.RVLite"), FoundationNavigationModule.RVLite, "", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("StreamaxConfigs.StreamaxConfigs"), FoundationNavigationModule.StreamaxConfigsAccountList, "", { showOnMobile: true });


        return navigationItems;
    }

    private createSoftwareNavigationItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        const navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("ConfigManagement.ConfigManagement"), FoundationNavigationModule.ConfigManager, "token", { showOnMobile: true });

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("AppManagement.AppManagement"), FoundationNavigationModule.AppManager, "token", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("CameraConfiguration.CameraConfiguration"), FoundationNavigationModule.CameraConfig, "token", { showOnMobile: true });

        return navigationItems;
    }

    private createDeviceHealthNavigationItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        var navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("DeviceHealth.Title"), FoundationNavigationModule.DeviceHealth, "monitor_heart", { showOnMobile: true });

        return navigationItems;
    }

    private createNavigationItemsForFoundationEngineers(navObject: INavigationDeclarationObject): INavigationItem[] {
        var navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("EngineerJobItemList.JobItems"), FoundationNavigationModule.JobItems, "work", { showOnMobile: true });

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("SideNavigation.History"), FoundationNavigationModule.EngineerHistory, "history", { showOnMobile: true });
        return navigationItems;
    }

    private createCustomerNavigationItems(navObject: INavigationDeclarationObject): INavigationItem[] {
        var navigationItems: INavigationItem[] = [];

        this.addNavigationItem(navigationItems, navObject, this.translationService.t("Common.Customers"), FoundationNavigationModule.Customers, "business", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("SideNavigation.Subscriptions"), FoundationNavigationModule.CustomerSubscriptions, "checklist", { showOnMobile: true });
        this.addNavigationItem(navigationItems, navObject, this.translationService.t("SideNavigation.NpsScores"), FoundationNavigationModule.NPS, "star", { showOnMobile: true });

        return navigationItems;
    }

    private updateNavItemCount(navigationItems: INavigationItem[], uiSref: string, count: number) {
        const foundItem = this.findNavItem(navigationItems, uiSref);

        if (foundItem) {
            foundItem.count = count;
        }
    }

    private findNavItem(navigationItems: INavigationItem[], uiSref: string): INavigationItem {
        if (!navigationItems) {
            return;
        }

        for (var i = 0; i < navigationItems.length; i++) {
            const item = navigationItems[i];

            if (item.uiSref == uiSref) {
                return item;
            }

            const foundItem = this.findNavItem(item.subNavItems, uiSref);

            if (foundItem) {
                return foundItem;
            }
        };

        return null;
    }

    private showNew(navItemName: string): boolean {
        const visitedPagesCookie = getCookie(this.visitedCookie);
        if (!visitedPagesCookie) {
            return true;
        }

        return !visitedPagesCookie.includes(navItemName);
    }
}
